import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const SUB_ID_KEY = 'find2day_sub_id';

/**
 * TownPill — Iter 188 (Feb 2026)
 *
 * Small button that surfaces the user's currently-selected home town.
 * Tapping it dispatches `find2day:open-location-picker` which the globally-
 * mounted RegionVillagePicker listens for — same opener path the Profile
 * page already uses. Zero new APIs, zero new state stores.
 *
 * Two variants:
 *   - default ("fixed"): floats top-left over content. Used as a global
 *     fallback on consumer routes that don't own a dedicated slot.
 *   - "inline": no fixed positioning, white/20 + white text — drops into a
 *     search/filter row. Used on /discover where it groups with the other
 *     discovery controls.
 *
 * Hidden on auth, admin, full-screen detail routes, and on the routes that
 * mount their own inline copy.
 */
const HIDDEN_PREFIXES = [
  '/admin',
  '/signin',
  '/signup',
  '/business/',          // covers /business/:id (detail), /business/signup, /business/dashboard
  '/profile',
  '/notifications',
  '/checkout',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/payment/',
  '/helper/',            // covers /helper/login, /helper/dashboard
  '/partners/',
  '/calendar',           // dedicated Calendar organiser area has its own header
  '/local',              // Home & Local Prices has its own in-page town selector
];

// Iter 188 — routes that mount their own inline TownPill in the search row.
// The global fixed instance is hidden on these so the user only sees one
// town control at a time.
const INLINE_HOST_PATHS = ['/discover', '/explore'];

const useHomeTown = () => {
  const [homeTown, setHomeTown] = useState('');

  const refresh = async () => {
    try {
      const subId = localStorage.getItem(SUB_ID_KEY);
      if (!subId) {
        setHomeTown('');
        return;
      }
      const r = await axios.get(`${API}/api/notifications/location-prefs`, {
        params: { subscriptionId: subId },
      });
      setHomeTown(r.data?.homeTown || '');
    } catch {
      setHomeTown('');
    }
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('find2day:location-prefs-saved', handler);
    return () => window.removeEventListener('find2day:location-prefs-saved', handler);
  }, []);

  return homeTown;
};

const TownPill = ({ variant = 'fixed' }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const homeTown = useHomeTown();

  // The fixed variant is suppressed on routes that mount their own inline
  // copy, on the homepage, and on full-screen detail / auth pages. It is also
  // hidden entirely once a town has been selected — the floating prompt only
  // exists to nudge first-time users; changing town later happens from the
  // Profile / location selector, never a floating bubble across the app.
  if (variant === 'fixed') {
    if (homeTown) return null;
    if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
    if (pathname === '/') return null;
    if (INLINE_HOST_PATHS.includes(pathname)) return null;
    // Iter 200 — Hide on Local Safety Alerts: the "Follow Your Town" CTA
    // already provides per-town context here, so the floating pill is
    // redundant and visually noisy.
    if (pathname.startsWith('/safety-bulletin')) return null;
  }

  const label = homeTown || t('townPill.choose', 'Choose your town');
  const aria = t('townPill.aria', 'Change your town or village');
  const openPicker = () =>
    window.dispatchEvent(new CustomEvent('find2day:open-location-picker'));

  if (variant === 'inline') {
    // White/20 to match the other header buttons on /discover; switches to
    // solid white when a town is set so it reads as a "filter active" chip
    // — same pattern as the Nearest Me button.
    const active = Boolean(homeTown);
    return (
      <button
        type="button"
        onClick={openPicker}
        className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 max-w-[40vw] sm:max-w-none ${
          active
            ? 'bg-white text-purple-600 font-semibold'
            : 'bg-white/20 hover:bg-white/30 text-white'
        }`}
        data-testid="town-pill-inline"
        aria-label={aria}
        title={label}
      >
        <MapPin className={`h-4 w-4 shrink-0 ${active ? 'text-purple-600' : ''}`} aria-hidden="true" />
        <span className="text-xs sm:text-sm truncate">{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={openPicker}
      className="fixed top-3 left-3 z-40 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                 bg-white/95 backdrop-blur shadow-md border border-gray-200
                 text-xs sm:text-sm font-semibold text-gray-800
                 hover:bg-white hover:shadow-lg active:scale-95 transition-all max-w-[55vw] sm:max-w-none"
      data-testid="town-pill"
      aria-label={aria}
    >
      <MapPin className="h-3.5 w-3.5 text-purple-600 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </button>
  );
};

export default TownPill;
